import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "../../../../app/hooks";
import { getParamAsInt } from "../../../../app/utils";
import { UserRole } from "../../../../app/userSlice";
import { scrollToSection, useScrollToSection } from "../../../../app/useScrollToSection";
import { AvatarWithName } from "../../../../components/Avatar";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import { Loading } from "../../../../components/Loading";
import { DownloadButton } from "../../shared/DownloadButton";
import { CrTraitScoreTable } from "./CrTraitScoreTable";
import { EssayTraitScoreTable } from "./EssayTraitScoreTable";
import { GenreScoreTable } from "./GenreScoreTable";
import { CrTraitScoreChart } from "./CrTraitScoreChart";
import { EssayTraitScoreChart } from "./EssayTraitScoreChart";
import { GenreScoreChart } from "./GenreScoreChart";
import { usePerformanceReportQuery } from "./performanceReportApiSlice";

export const STUDENT_TRAIT_AND_TOTAL_SCORES = "trait-and-total-scores";
export const STUDENT_WRITING_GENRE_SCORES = "writing-genre-scores";
export const STUDENT_CR_SCORES = "constructed-response-scores";

export default function StudentPerformanceReport() {
	const [queryParams] = useSearchParams();
	const writerIdQueryParam = getParamAsInt(queryParams, "writerId");
	const courseIdQueryParam = getParamAsInt(queryParams, "courseId");
	const sectionQueryParam = queryParams.get("section");

	const { aspUrl, config } = useAppSelector((state) => state.site);
	const { userId, course, role } = useAppSelector((state) => state.user);

	const writerId = writerIdQueryParam ?? userId!;
	const courseId = courseIdQueryParam ?? course!.id;

	const result = usePerformanceReportQuery({ writerId, courseId });
	const data = result.data;

	const [view, setView] = useState<string>("react");

	useScrollToSection();
	sectionQueryParam && scrollToSection(sectionQueryParam);

	const Heading = () => <h1>{role !== UserRole.Writer ? "Student " : ""}Performance Report</h1>;
	const Subheading = () =>
		role === UserRole.Writer || !data ? (
			""
		) : (
			<h2 className="mb-4 font-medium">
				<AvatarWithName
					userId={data.writerId}
					name={data.writerName}
					initials={data.writerInitials ?? "?"}
					hasImage={data.writerHasImage}
				/>
			</h2>
		);

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<Subheading />
					<Heading />
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "react", label: "React" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "react" && (
				<div>
					<section id={STUDENT_TRAIT_AND_TOTAL_SCORES} className="pt-4">
						<h2>
							{config?.isUtahComposeSite ? "Analytical" : "Trait"} and Total Scores
						</h2>
						<p>
							The report below displays the average score for each writing{" "}
							{config?.isUtahComposeSite ? "element" : "trait"}, total score, and
							teacher-scored textual evidence and content accuracy categories. The
							averages are calculated using the score from the most recent draft of
							each essay. To view the Score Report for an essay, click on the date.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex mt-4 place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadEssayScoresFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.averageTraitScoresByEssay.length == 0}
									/>
								</div>
								<EssayTraitScoreTable
									className="mt-4"
									scores={data.averageTraitScoresByEssay}
									averages={data.averageTraitScoresForWriter}
								/>
								<EssayTraitScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									scores={data.averageTraitScoresByEssay}
									averages={data.averageTraitScoresForWriter}
								/>
							</>
						)}
					</section>
					<section id={STUDENT_CR_SCORES} className="pt-8">
						<h2>Constructed Response Scores</h2>
						<p>
							The report below displays the average holistic score as well as
							teacher-scored textual evidence and content accuracy categories. The
							averages are calculated using the score from the most recent draft of
							each response. To view the Score Report for an essay, click on the date.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex mt-4 place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadConstructedResponseEssayScoresFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={
											data.averageConstructedResponseScoresByEssay.length == 0
										}
									/>
								</div>
								<CrTraitScoreTable
									className="mt-4"
									scores={data.averageConstructedResponseScoresByEssay}
									averages={data.averageConstructedResponseScoresForWriter}
								/>
								<CrTraitScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									scores={data.averageConstructedResponseScoresByEssay}
									averages={data.averageConstructedResponseScoresForWriter}
								/>
							</>
						)}
					</section>
					<section id={STUDENT_WRITING_GENRE_SCORES} className="pt-8">
						<h2>Writing Genre Scores</h2>
						<p>
							The report below compares the average score on essays designated to a
							specific genre of writing for a student.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex mt-4 place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadStandardScoresFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.averageStandardScoresByEssay.length == 0}
									/>
								</div>
								<GenreScoreTable
									className="mt-4"
									scores={data.averageStandardScoresByEssay}
									averages={data.averageStandardScoresForWriter}
								/>
								<GenreScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									data={data.averageStandardScoresForWriter}
								/>
							</>
						)}
					</section>
				</div>
			)}
			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Performance Report Data" result={result} />
				</>
			)}
		</section>
	);
}
